import type { AnyRoute } from '@tanstack/react-router';
import { RouteErrorComponent } from './components/RouteErrorComponent';
import type { createAuthenticatedRoute } from './pages/_authenticated';
import type { AuthenticatedGuard } from './pages/_authenticated.guard';
import type { Route as bareRoute } from './pages/_bare';
import type { createPublicRoute } from './pages/_public';

// Re-export route-definition APIs so that domain route.manifest files can import
// from this module directly, avoiding a circular dependency through the barrel.
export { createRoute, lazyRouteComponent, notFound, redirect, retainSearchParams } from '@tanstack/react-router';

export type DomainParents = {
  authenticated: ReturnType<typeof createAuthenticatedRoute>;
  public: ReturnType<typeof createPublicRoute>;
  bare: typeof bareRoute;
};

export type DomainRouteManifest = {
  authenticated?: readonly AnyRoute[];
  public?: readonly AnyRoute[];
  bare?: readonly AnyRoute[];
  /**
   * Guards this domain contributes to the `_authenticated` layout's beforeLoad.
   * Run after authentication succeeds, in the order domains are registered in
   * `routeTree.ts`. Throw a `redirect(...)` to abort navigation.
   */
  authenticatedGuards?: readonly AuthenticatedGuard[];
  /** Same, for the `_public` layout. These also run for anonymous visitors, so each guard decides on the auth status itself. */
  publicGuards?: readonly AuthenticatedGuard[];
};

// Contain loader/render failures (network, non-2xx, schema mismatch) at the
// route level instead of letting them bubble to the app shell. Routes render
// inside the authenticated layout's <Page> chrome, so the default is an
// inline error block — not a full-page error UI (which would visually nest a
// second error page beneath the still-rendered page header). Routes can opt
// into a custom errorComponent; the router-level fallback in `router.tsx`
// still uses DefaultErrorComponent for failures outside route bounds.
//
// Recurses into `route.children` so nested routes (e.g. an index/edit pair
// composed under a layout route via `addChildren`) inherit the default too —
// only the top-level array reaches this function, but every route in the
// subtree benefits.
function applyDefaultErrorComponent(route: AnyRoute) {
  if (!route.options.errorComponent) {
    route.options.errorComponent = RouteErrorComponent;
  }
  for (const child of (route.children ?? []) as AnyRoute[]) {
    applyDefaultErrorComponent(child);
  }
}

export function defineDomainRoutes<const TManifest extends DomainRouteManifest>(manifest: TManifest) {
  for (const route of [...(manifest.authenticated ?? []), ...(manifest.public ?? [])]) {
    applyDefaultErrorComponent(route);
  }

  return manifest;
}
